/** @jsxImportSource @emotion/react */

import {css} from "@emotion/react";
import {useCallback, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {
  QuoteDocument,
  deleteQuoteDocument,
  listQuoteDocuments,
  uploadQuoteDocument,
} from "api/quote";
import {Box} from "components/Box";
import {H4} from "components/elements";
import Loading from "components/Loading";

const ACCEPTED_FILE_TYPES = [".pdf", ".jpg", ".jpeg", ".png"];

export default function AcordUpload({quoteId}: {quoteId: string}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {data: documents, isLoading: docsLoading} = useQuery(
    ["listQuoteDocuments", quoteId],
    () => listQuoteDocuments(quoteId)
  );

  const uploadMutation = useMutation(
    async (file: File) => uploadQuoteDocument(quoteId, file),
    {
      onSuccess: () => {
        setUploadError(null);
        queryClient.invalidateQueries(["listQuoteDocuments", quoteId]);
      },
      onError: (error: any) => {
        setUploadError(
          typeof error === "string" ? error : "Failed to upload file"
        );
      },
    }
  );

  const deleteMutation = useMutation(
    async (documentId: number) => deleteQuoteDocument(quoteId, documentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listQuoteDocuments", quoteId]);
      },
    }
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      if (!ACCEPTED_FILE_TYPES.includes(ext)) {
        setUploadError(
          `File type ${ext} is not allowed. Only PDF, JPG, and PNG files are accepted.`
        );
        return;
      }
      setUploadError(null);
      uploadMutation.mutate(file);
    },
    [uploadMutation]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <Box mb={100}>
      <H4>Upload Acord Application</H4>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        css={css`
          border: 2px dashed ${isDragging ? "#007bff" : "#ccc"};
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s;
          margin: 16px 0;
          &:hover {
            border-color: #999;
          }
        `}
      >
        {uploadMutation.isLoading ? (
          <Loading>
            <span>Uploading...</span>
          </Loading>
        ) : (
          <>
            <p css={css`margin: 0; font-size: 16px; color: #666;`}>
              Drag and drop your Acord application here, or click to browse
            </p>
            <p css={css`margin: 8px 0 0; font-size: 12px; color: #999;`}>
              Only PDF, JPG, and PNG files are accepted
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES.join(",")}
          onChange={handleInputChange}
          css={css`display: none;`}
        />
      </div>

      {uploadError && (
        <p css={css`color: #d32f2f; font-size: 14px; margin: 8px 0;`}>
          {uploadError}
        </p>
      )}

      {docsLoading ? (
        <Loading>
          <span>Loading documents...</span>
        </Loading>
      ) : (
        documents &&
        documents.length > 0 && (
          <Box mt={16}>
            <H4>Uploaded Documents</H4>
            {documents.map((doc: QuoteDocument) => (
              <div
                key={doc.id}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 12px 16px;
                  border: 1px solid #e0e0e0;
                  border-radius: 4px;
                  margin-bottom: 8px;
                `}
              >
                <a
                  href={doc.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  css={css`
                    color: #007bff;
                    text-decoration: none;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                >
                  {doc.file_name}
                </a>
                <button
                  onClick={() => deleteMutation.mutate(doc.id)}
                  disabled={deleteMutation.isLoading}
                  css={css`
                    background: none;
                    border: none;
                    color: #d32f2f;
                    cursor: pointer;
                    font-size: 14px;
                    &:hover {
                      text-decoration: underline;
                    }
                    &:disabled {
                      opacity: 0.5;
                      cursor: not-allowed;
                    }
                  `}
                >
                  Delete
                </button>
              </div>
            ))}
          </Box>
        )
      )}
    </Box>
  );
}
